import React, { FC, useMemo, useRef, useContext } from 'react';
import { SingleDateHeaderProps } from './singleDateHeader.types';
import { StyledDatesHeaderDiv, StyledDateBoxDiv } from './datesHeader.styles';
import moment from 'moment';
import { useConditionalPageContextSERP } from '../../../../../../hooks';
import { ContentContext } from '../../index.contexts';
import { getDateText } from './utils';
import { getSearchFormClickTrackingLoc } from '../../../../../../modules/utils/searchFormHelper';

interface SingleDateBoxProps {
  label: string;
  date: string;
  onClearHandler: () => void;
  onClickHandler: () => void;
  focused: boolean;
  monthSelected: number | undefined;
  yearSelected: number | undefined;
  showIcon: boolean | undefined;
}

const SingleDateBox: React.FC<SingleDateBoxProps> = ({
  label,
  date,
  onClearHandler,
  onClickHandler,
  focused,
  monthSelected,
  yearSelected,
  showIcon,
}) => {
  const { clear, selectAMonth, flexibleDateFormatLabel } = useContext(ContentContext);
  const { currentLocale } = useConditionalPageContextSERP();
  const mainBtn = useRef<HTMLButtonElement>(null);

  const clearClickHandler = (): void => {
    setTimeout(onClearHandler, 0);
    if (window.navigator?.platform?.indexOf('Win') > -1) {
      mainBtn.current?.setAttribute('aria-live', 'polite');
    }
    mainBtn.current?.focus();
  };

  const dateText = useMemo(
    () => getDateText(monthSelected, yearSelected, currentLocale, selectAMonth, flexibleDateFormatLabel),
    [monthSelected, yearSelected, currentLocale, selectAMonth, flexibleDateFormatLabel]
  );

  return (
    <StyledDateBoxDiv className={focused ? 'shop-button-focus' : ''}>
      <button
        type="button"
        ref={mainBtn}
        aria-expanded={focused}
        aria-live={focused ? 'polite' : 'off'}
        className="custom_click_track"
        {...{
          custom_click_track_value: `${getSearchFormClickTrackingLoc()}| Flexible Date Picker ${label} button |internal`,
        }}
        onClick={onClickHandler}
      >
        <span className="t-label-xs">{label}</span>
        <span className={`h-r-form-field-txt ${!date ? 'gray-shade' : ''}`}>{dateText}</span>
      </button>
      {focused &&
      monthSelected &&
      (monthSelected.toString() + ' ' + yearSelected?.toString() !==
        (moment().month() + 1).toString() + ' ' + moment().year().toString() ||
        showIcon) ? (
        <button
          type="button"
          className="link-white icon-cancel cancel-button custom_click_track"
          {...{
            custom_click_track_value: `${getSearchFormClickTrackingLoc()}| Flexible Date Picker ${label} clear button |internal`,
          }}
          onClick={clearClickHandler}
        >
          <span className="sr-only">{clear}</span>
        </button>
      ) : (
        ''
      )}
    </StyledDateBoxDiv>
  );
};

const SingleDateHeader: FC<SingleDateHeaderProps> = ({
  checkInDate,
  onClearCheckIn,
  focusedCheckIn,
  onClickCheckIn,
  yearSelected,
  monthSelected,
  showIcon,
}) => {
  const { flexibleDates } = useContext(ContentContext);

  return (
    <StyledDatesHeaderDiv>
      <SingleDateBox
        label={flexibleDates}
        date={checkInDate}
        onClearHandler={onClearCheckIn}
        onClickHandler={onClickCheckIn}
        focused={focusedCheckIn}
        monthSelected={monthSelected}
        yearSelected={yearSelected}
        showIcon={showIcon}
      />
    </StyledDatesHeaderDiv>
  );
};

export default SingleDateHeader;
